import React, {useMemo, useState} from 'react'
import {Chart} from "primereact/chart";
import {anzahlAufnahmen, AUFNAHMEN_BEREICHE, FARBEN, getAchsen, getDiagrammOptionen, getSpielerZeilen, legQuote, useSchmaleAnsicht, vorhanden, zahl} from "./statistikWerte";

// hoeherBesser: false, wenn der kleinere Wert besser ist (z.B. Darts für das beste Leg)
const WERTE = [
    {id: "average", label: "Average", wert: s => Number(s.average), anzeige: w => zahl(w)},
    {id: "first9Average", label: "First 9 Average", wert: s => Number(s.first9Average), anzeige: w => zahl(w)},
    {id: "legquote", label: "Leg-Quote", wert: s => legQuote(s), anzeige: w => zahl(w, 0) + " %"},
    {id: "spiele", label: "Gewonnene Spiele", wert: s => s.gewonneneSpiele, anzeige: w => w},
    {id: "legs", label: "Gewonnene Legs", wert: s => s.gewonneneLegs, anzeige: w => w},
    {id: "bestLeg", label: "Best Leg (Darts)", wert: s => s.bestLeg, anzeige: w => w, hoeherBesser: false},
    {id: "_100Plus", label: "100+", wert: s => s._100Plus, anzeige: w => w},
    {id: "_140Plus", label: "140+", wert: s => s._140Plus, anzeige: w => w},
    {id: "_171er", label: "171", wert: s => s._171er, anzeige: w => w},
    {id: "_180er", label: "180", wert: s => s._180er, anzeige: w => w},
];

/**
 * Zwei Spieler nebeneinander vergleichen: Kennzahlen als gegenüberliegende Balken und
 * die Verteilung der Aufnahmen als Diagramm.
 */
export default function VergleichBereich({participants, playerStatistiks}) {

    // nur Spieler, die schon geworfen haben
    const spieler = useMemo(() => getSpielerZeilen(participants, playerStatistiks)
        .filter(zeile => vorhanden(zeile.statistik))
        .sort((a, b) => a.name.localeCompare(b.name)), [participants, playerStatistiks]);

    // vorbelegt mit den beiden Spielern mit dem besten Average
    const vorschlag = useMemo(() => [...spieler].sort((a, b) => Number(b.statistik.average) - Number(a.statistik.average)), [spieler]);
    const [idA, setIdA] = useState(undefined);
    const [idB, setIdB] = useState(undefined);
    const schmal = useSchmaleAnsicht();

    if (spieler.length < 2) {
        return <div className="dhpt-statistik">
            <p className="dhpt-hinweis">Für einen Vergleich werden mindestens zwei Spieler mit Statistik benötigt.</p>
        </div>
    }

    const spielerA = spieler.find(s => s.id === idA) || vorschlag[0];
    const spielerB = spieler.find(s => s.id === idB) || vorschlag.find(s => s.id !== spielerA.id);

    const tauschen = () => {
        setIdA(spielerB.id);
        setIdB(spielerA.id);
    }

    const getAuswahl = (id, wert, onChange, andererId, klasse) => {
        return <label className={"dhpt-st-auswahl " + klasse}>
            <span className="dhpt-st-versteckt">{id === "a" ? "Erster Spieler" : "Zweiter Spieler"}</span>
            <select value={wert} onChange={(event) => onChange(event.target.value)}>
                {spieler.map(s => <option key={s.id} value={s.id} disabled={s.id === andererId}>{s.name}</option>)}
            </select>
        </label>
    }

    const vergleiche = (eintrag) => {
        const a = eintrag.wert(spielerA.statistik);
        const b = eintrag.wert(spielerB.statistik);
        const hatA = vorhanden(a) && !isNaN(a);
        const hatB = vorhanden(b) && !isNaN(b);
        let besser = undefined;
        if (hatA && hatB && a !== b) {
            const aBesser = eintrag.hoeherBesser === false ? a < b : a > b;
            besser = aBesser ? "a" : "b";
        }
        // Anteil der Balken: beim kleineren-ist-besser-Wert umgekehrt, damit der bessere Spieler den längeren Balken hat
        let anteilA = 50;
        if (hatA && hatB && a + b > 0) {
            anteilA = eintrag.hoeherBesser === false ? b / (a + b) * 100 : a / (a + b) * 100;
        }
        return {eintrag, a, b, hatA, hatB, besser, anteilA};
    }

    const ergebnisse = WERTE.map(vergleiche);
    const besserA = ergebnisse.filter(e => e.besser === "a").length;
    const besserB = ergebnisse.filter(e => e.besser === "b").length;

    const getWertZeile = ({eintrag, a, b, hatA, hatB, besser, anteilA}) => {
        return <li key={eintrag.id} className="dhpt-st-duell">
            <span className={"dhpt-st-duell-wert dhpt-st-duell-links" + (besser === "a" ? " dhpt-st-besser" : "")}>{hatA ? eintrag.anzeige(a) : "–"}</span>
            <span className="dhpt-st-duell-label">{eintrag.label}</span>
            <span className="dhpt-st-duell-balken" aria-hidden="true">
                <span className="dhpt-st-duell-a" style={{width: anteilA + "%"}}/>
                <span className="dhpt-st-duell-b" style={{width: (100 - anteilA) + "%"}}/>
            </span>
            <span className={"dhpt-st-duell-wert dhpt-st-duell-rechts" + (besser === "b" ? " dhpt-st-besser" : "")}>{hatB ? eintrag.anzeige(b) : "–"}</span>
        </li>
    }

    const getBilanz = () => {
        return <div className="dhpt-st-bilanz" aria-label={"Bessere Werte: " + spielerA.name + " " + besserA + ", " + spielerB.name + " " + besserB}>
            <span className="dhpt-st-bilanz-zahl dhpt-st-bilanz-a">{besserA}</span>
            <span className="dhpt-st-bilanz-text">bessere Werte</span>
            <span className="dhpt-st-bilanz-zahl dhpt-st-bilanz-b">{besserB}</span>
        </div>
    }

    const getVerteilung = () => {
        // Anteil in Prozent, damit Spieler mit unterschiedlich vielen Spielen vergleichbar sind
        const anteile = (statistik) => {
            const gesamt = anzahlAufnahmen(statistik);
            return AUFNAHMEN_BEREICHE.map(bereich => gesamt > 0 ? Math.round((statistik[bereich.key] || 0) / gesamt * 1000) / 10 : 0);
        };
        const data = {
            labels: AUFNAHMEN_BEREICHE.map(bereich => bereich.label),
            datasets: [
                {label: spielerA.name, data: anteile(spielerA.statistik), backgroundColor: FARBEN.spielerA, borderRadius: 3},
                {label: spielerB.name, data: anteile(spielerB.statistik), backgroundColor: FARBEN.spielerB, borderRadius: 3},
            ],
        };
        const achsen = getAchsen("Anteil in %");
        achsen.y.ticks.precision = undefined;
        // auf dem Handy liegen die Balken waagrecht, sonst werden 19 Bereiche mit je zwei Balken zu schmal
        const waagrecht = schmal;
        const scales = waagrecht ? {x: {...achsen.y, position: "top"}, y: {...achsen.x, title: undefined}} : achsen;
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>Verteilung der Aufnahmen</span><span className="dhpt-karte-zusatz">Anteil an allen Aufnahmen</span></h3>
            <div className={"dhpt-st-balken-diagramm" + (waagrecht ? " dhpt-st-balken-waagrecht" : "")} role="img"
                 aria-label={"Verteilung der Aufnahmen von " + spielerA.name + " und " + spielerB.name}>
                <Chart key={waagrecht ? "waagrecht" : "senkrecht"} type="bar" data={data} style={{height: "100%"}}
                       options={getDiagrammOptionen({
                           scales,
                           plugins: {
                               tooltip: {callbacks: {label: (kontext) => kontext.dataset.label + ": " + zahl(waagrecht ? kontext.parsed.x : kontext.parsed.y, 1) + " %"}},
                           },
                           weitere: waagrecht ? {indexAxis: "y"} : {},
                       })}/>
            </div>
        </section>
    }

    return <div className="dhpt-statistik">
        <section className="dhpt-karte">
            <div className="dhpt-st-auswahl-zeile">
                {getAuswahl("a", spielerA.id, setIdA, spielerB.id, "dhpt-st-auswahl-a")}
                <button type="button" className="dhpt-st-tauschen" aria-label="Spieler tauschen" title="Spieler tauschen" onClick={tauschen}>
                    <i className="pi pi-arrow-right-arrow-left" aria-hidden="true"/>
                </button>
                {getAuswahl("b", spielerB.id, setIdB, spielerA.id, "dhpt-st-auswahl-b")}
            </div>
            {getBilanz()}
            <ul className="dhpt-st-duelle">
                {ergebnisse.map(getWertZeile)}
            </ul>
        </section>
        {getVerteilung()}
    </div>
}
